import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from "react-redux";
import { callNotify } from '../../..';
import { PlanProvider } from "../../../contexts/PlanOperationContext/Plan";
import { VesselSideService } from "../../../util/services/vessel-side";
import { selectOverview, selectPermissions } from "../../../util/store/selectors";
import { AppState, getStore } from "../../../util/store/store";
import {
    aWeekMillisecond,
    OPERATION_TYPE
} from "../../../util/varibles/constants";
import {
    changeLoadingOp,
    cloneObj,
    getElById,
    jumpToTarget,
    showErrorResponse
} from "../../../util/varibles/global";
import { IOperation, IOperation2, IVessel } from "../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../util/varibles/message";
import { ACTION, VIEW_MODE } from '../../../util/varibles/permission';
import { template, TemplateCode } from "../../../util/varibles/template";
import { loginActions } from "../../LoginPage/reducer";

import { EL_RULE } from '../../PlanOperationPage/Plan/Calendar/constants';
import { HEIGHT_PER_DAY } from '../../PlanOperationPage/Plan/Calendar/Operations/constants';
import EmptyVessels from "../../PlanOperationPage/Plan/Calendar/Vessels/EmptyVessels";

import Weeks from "../../PlanOperationPage/Plan/Calendar/Weeks";
import { checkValidOperation, generate } from "../../PlanOperationPage/util";
import { VALIDATION_STATUS } from "../../PlanOperationPage/util/validation";
import { CALENDAR_ID } from "../constants";
import { overviewActions } from "../reducer";
import Header from "./Header";

import Menu from './Menu';
import styles from "./style.module.scss";
import Vessels from './Vessels';


const mapStateToProps = (state: AppState) => ({
    weeks: state.overview.weeks,
    vessels: state.overview.vessels,
    loading: state.overview.loadingData,
    default_vessel: state.login.user.default_vessel,
})


interface IProps {
    weeks: any
    vessels: IVessel[]
    loading: boolean
    default_vessel?: string

    addOps: typeof overviewActions.addOps

    deleteOps: typeof overviewActions.deleteOps

    updateOps: typeof overviewActions.updateOps
}

const CalendarVessel: React.FC<IProps> = (props) => {
    const [vessel, setVessel] = useState<IVessel>();
    const { weeks, vessels, default_vessel } = props;
    const dispatch = useDispatch();
    const showAllPlans = useSelector(selectOverview.showAllPlans);
    const [canUseVesselMenu, canUseOperationMenu, canUpdateOperation] = useSelector(selectPermissions([
        ACTION.OVERVIEW.VESSEL_MENU,
        ACTION.OVERVIEW.OP_MENU,
        ACTION.OVERVIEW.UPDATE_OP,
    ]));

    const startTime = weeks[0].time;
    const finishTime = weeks[weeks.length - 1].time + aWeekMillisecond - 1;
    const calendarRef = useRef<HTMLDivElement>(null);
    const didInitialScroll = useRef(false);

    useEffect(() => {
        const vessel = vessels.find(item => item.id === default_vessel) || vessels[0];
        setVessel(vessel);
        if (vessel && !didInitialScroll.current) {
            didInitialScroll.current = true;
            jumpToTarget(CALENDAR_ID);
        }
    }, [vessels, default_vessel]);

    const handleDragOp = (op: IOperation) => {
        const { id } = op.operation;
        const { operations, level } = selectOverview.root(getStore())
        const old = cloneObj(operations[id]);
        if (op.operation.operation_type !== OPERATION_TYPE.EVENT) {
            notify.error(NotifyCode.E34)()
            props.deleteOps([id]);
            setTimeout(() => props.addOps([old]), 4)
            return;
        }
        const { status, message } = checkValidOperation({
            operations,
            level,
            data: op,
            factories: [],
            allowPast: true,
        });
        if (status === VALIDATION_STATUS.VALID) {
            const vessels = [
                {
                    vessel_id: op.vessel.id,
                    vessel_owner_id: op.vessel.tenant_id,
                    operations: [generate(op as IOperation2)]
                }
            ]
            props.deleteOps([id]);
            setTimeout(() => {
                props.addOps([op]);
                setTimeout(() => {
                    const el = getElById(id + '-loading');
                    changeLoadingOp(el, true);
                }, 1)
            }, 4)
            VesselSideService.crewSend({ vessels })
                .then((rs: any) => {
                    const { new_operations, old_operations } = rs;
                    const { vessel } = op;

                    const { newCode, newOps } = new_operations.reduce((rs: any, item: any) => {
                        rs.newCode = [...rs.newCode, `#${item.operation.operation_code}`];
                        rs.newOps = [...rs.newOps, { ...item, vessel }];
                        return rs;
                    }, { newCode: [], newOps: [] });

                    const { oldCode, oldOps } = old_operations.reduce((rs: { oldCode: string[], oldOps: IOperation[] }, item: IOperation) => {
                        rs.oldCode = [...rs.oldCode || [], `#${item.operation.operation_code}`];
                        rs.oldOps = [...rs.oldOps || [], { ...item, vessel }];
                        return rs;
                    }, { oldCode: [], oldOps: [] });

                    notify.success({
                        message: 'Update successfully',
                        description: template[TemplateCode.T2]('', newCode, oldCode)
                    });
                    const el = getElById(id + '-loading');
                    changeLoadingOp(el, false);
                    props.updateOps(oldOps);
                    dispatch(loginActions.updateOpsOfVessel([...newOps, ...oldOps]));
                })
                .catch(error => {
                    const el = getElById(id + '-loading');
                    changeLoadingOp(el, false);
                    showErrorResponse('Update failed', error)
                    props.deleteOps([id]);
                    setTimeout(() => props.addOps([old]), 5)
                })
        } else {
            callNotify?.error({ title: 'Update failed', description: message });
            props.deleteOps([id]);
            setTimeout(() => props.addOps([old]), 4)
        }
    }

    const preventNonEventDrag = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        const target = event.target as HTMLElement;
        const operation = target.closest<HTMLElement>(`[data-rule="${EL_RULE.OPERATION}"]`);

        if (operation && operation.dataset.operationType !== `${OPERATION_TYPE.EVENT}`) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    if (!vessel)
        return <>
            <Header />
            <div id={CALENDAR_ID} className={styles.calendar}>
                <Weeks startTime={startTime} finishTime={finishTime} />
                <EmptyVessels />
            </div>
        </>

    return <>
        <Header />
        <div
            id={CALENDAR_ID}
            ref={calendarRef}
            className={styles.calendar}
            data-show-all-plans={showAllPlans}
            style={{
                '--begin': startTime,
                '--height-day': `${HEIGHT_PER_DAY}px`,
                '--calendar-right-offset': showAllPlans ? '48px' : '0px',
            } as any}
            onMouseDownCapture={preventNonEventDrag}
        >
            <Weeks startTime={startTime} finishTime={finishTime} />
            <PlanProvider
                startCalendar={startTime}
                endCalendar={finishTime}
                calendarRef={() => calendarRef.current as HTMLDivElement}
                onDragOp={handleDragOp}
                mode={VIEW_MODE.VESSEL}
                permission={{
                    VESSEL_MENU: canUseVesselMenu,
                    OP_MENU: canUseOperationMenu,
                    UPDATE_OP: canUpdateOperation,
                }}
            >
                <Menu vessel={vessel}>
                    <Vessels vessel={vessel} />
                </Menu>
            </PlanProvider>
        </div>
    </>;
};
export default connect(mapStateToProps, {
    deleteOps: overviewActions.deleteOps,
    addOps: overviewActions.addOps,
    updateOps: overviewActions.updateOps
})(CalendarVessel);
